import React, { useState, useMemo } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { api } from '../services/api';
import { ChevronRight, DollarSign, Download, Plus, UploadCloud, Search, RefreshCw, X } from 'lucide-react';
import CreateJobModal from '../components/CreateJobModal';
import { fmtClock, businessToday, isoPlusDays } from '../utils/time';

/**
 * Jobs — one flat list of every job (mows, one-off work, projects, to-dos).
 *
 * The owner, Nov 2 2026: "remove the jobs by route and just do all jobs." The
 * by-route accordion is gone; the route lives on each row as a colored chip and
 * as a filter. Every filter is in the URL (?when=&date=&q=&status=&type=&route=)
 * so a link reproduces the view, and ?filter=unpaid / ?new=1 keep working.
 */

const statusBadge: Record<string, string> = {
  scheduled: 'badge-scheduled',
  in_progress: 'badge-in_progress',
  complete: 'badge-complete',
  skipped: 'badge-skipped',
};

type When = 'day' | 'week' | 'month' | 'upcoming' | 'past';
const WHEN_OPTIONS: { key: When; label: string }[] = [
  { key: 'day', label: 'Day' },
  { key: 'week', label: 'Week' },
  { key: 'month', label: 'Month' },
  { key: 'upcoming', label: 'All upcoming' },
  { key: 'past', label: 'Past' },
];
const isWhen = (v: string | null): v is When => !!v && WHEN_OPTIONS.some(o => o.key === v);

type JobKind = 'mowing' | 'oneoff' | 'project' | 'todo';
const KIND_LABEL: Record<JobKind, string> = { mowing: 'Mowing', oneoff: 'One-off', project: 'Project', todo: 'To-do' };
const KIND_CHIP: Record<JobKind, string> = {
  mowing: 'bg-green-50 text-green-700 border-green-200',
  oneoff: 'bg-blue-50 text-blue-700 border-blue-200',
  project: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  todo: 'bg-gray-100 text-gray-700 border-gray-200',
};

/**
 * What kind of job a row is, for the Type filter and chip. A to-do is its own
 * service type (services/todos). A mow is anything mow-shaped, or a visit of a
 * schedule with no type of its own. A project is typed 'project' or came off a
 * bid (quote_id) without being a mow. Everything else is one-off work —
 * treatments, bush work, mulch.
 */
function jobKind(job: any): JobKind {
  const st = String(job.service_type || '').toLowerCase();
  if (st === 'todo') return 'todo';
  if (/mow/.test(st) || (!st && job.recurring_schedule_id)) return 'mowing';
  if (st === 'project' || job.quote_id) return 'project';
  return 'oneoff';
}

function fmtDate(iso: string) {
  return new Date(iso.slice(0, 10) + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

function freqLabel(frequency: string | null, customDays: number | null): string {
  if (!frequency) return '';
  if (frequency === 'weekly') return 'Weekly';
  if (frequency === 'biweekly') return 'Every 2 wks';
  const d = Number(customDays);
  if (d && d % 7 === 0) return `Every ${d / 7} wks`;
  return d ? `Every ${d} days` : 'Custom';
}

function fmtShort(iso: string | null | undefined) {
  return iso ? new Date(iso.slice(0, 10) + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '';
}

/** Colored route chip so the flat list still answers "which route is this on?" */
function RouteTag({ name, color }: { name?: string | null; color?: string | null }) {
  if (!name) return null;
  return (
    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-semibold text-snow max-w-[10rem] truncate"
      style={{ backgroundColor: color || '#7c3aed' }}>
      {name}
    </span>
  );
}

function fmtServiceType(st: string | null | undefined) {
  if (!st) return '';
  const map: Record<string, string> = {
    mowing: 'Mowing', project: 'Project', landscaping: 'Landscaping',
    mulch: 'Mulch', cleanup: 'Cleanup', irrigation: 'Irrigation',
    fence: 'Fence', trimming: 'Bush Trimming', bush_trimming: 'Bush Trimming',
    fertilizer: 'Fertilizer', aeration: 'Aeration', seeding: 'Seeding',
    sod: 'Sod', other: 'Other', todo: 'To-do', treatment: 'Lawn treatment',
  };
  const key = st.toLowerCase();
  return map[key] || st.split(/[_\s]+/).map(w => w[0].toUpperCase() + w.slice(1)).join(' ');
}

const selectCls = 'border border-gray-300 rounded-md text-sm px-3 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500';

export default function Jobs() {
  const qc = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const today = businessToday();

  // The dashboard links here with ?filter=unpaid — finished work nobody has been
  // credited for. The server applies the same pay-ledger rule the dashboard
  // counts with, so the date filter steps aside and the two always agree.
  const unpaidOnly = searchParams.get('filter') === 'unpaid';

  // Every filter reads from the URL so a link reproduces the view.
  const whenParam = searchParams.get('when');
  const when: When = isWhen(whenParam) ? whenParam : 'upcoming';
  const dateParam = searchParams.get('date') || '';
  const dayDate = /^\d{4}-\d{2}-\d{2}$/.test(dateParam) ? dateParam : today;
  const search = searchParams.get('q') || '';
  const statusFilter = searchParams.get('status') || '';
  const typeParam = searchParams.get('type') || '';
  const typeFilter: JobKind | '' = (['mowing', 'oneoff', 'project', 'todo'] as string[]).includes(typeParam) ? typeParam as JobKind : '';
  const routeFilter = searchParams.get('route') || ''; // route id, or 'none'

  function setParam(key: string, value: string) {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value); else next.delete(key);
    // A day only means something under the Day filter.
    if (key === 'when' && value !== 'day') next.delete('date');
    setSearchParams(next, { replace: true });
  }

  // /jobs/new (and /jobs?new=1) land here with the modal already open, so the
  // deep link works the same as the New Job button.
  const [showCreate, setShowCreate] = useState(searchParams.get('new') === '1');
  const [sheetsExporting, setSheetsExporting] = useState(false);
  const [sheetsMsg, setSheetsMsg] = useState<string | null>(null);

  // Drop ?new=1 on close, or the next visit to /jobs re-opens the modal.
  const closeCreate = () => {
    setShowCreate(false);
    if (searchParams.get('new')) {
      const next = new URLSearchParams(searchParams);
      next.delete('new');
      setSearchParams(next, { replace: true });
    }
  };

  // The date window each filter asks the server for (GET /jobs?from=&to=).
  // Day is the picked day (today by default); Week and Month roll forward from
  // today so they are never a dead calendar week on a Sunday night.
  const range = useMemo((): { from?: string; to?: string; label: string } => {
    if (unpaidOnly) return { label: 'finished, nobody paid' };
    switch (when) {
      case 'day': return { from: dayDate, to: dayDate, label: fmtDate(dayDate) };
      case 'week': { const to = isoPlusDays(today, 6); return { from: today, to, label: `${fmtShort(today)} – ${fmtShort(to)}` }; }
      case 'month': { const to = isoPlusDays(today, 29); return { from: today, to, label: `${fmtShort(today)} – ${fmtShort(to)}` }; }
      case 'past': return { to: isoPlusDays(today, -1), label: `before ${fmtShort(today)}` };
      default: return { from: today, label: `from ${fmtShort(today)}` };
    }
  }, [when, dayDate, today, unpaidOnly]);

  const { data: jobsData, isLoading } = useQuery(['jobs-list', range.from || '', range.to || '', statusFilter, unpaidOnly], () =>
    api.get('/jobs', {
      params: {
        ...(range.from ? { from: range.from } : {}),
        ...(range.to ? { to: range.to } : {}),
        ...(statusFilter ? { status: statusFilter } : {}),
        ...(unpaidOnly ? { unpaid: '1' } : {}),
      }
    }).then(r => r.data.jobs as any[])
  );

  const { data: routesData } = useQuery(['routes-list-jobs'], () =>
    api.get('/routes').then(r => r.data.routes as any[])
  );
  const routeOptions = useMemo(() => {
    const byId = new Map<number, string>();
    for (const r of routesData || []) byId.set(Number(r.id), r.name);
    // A route the list shows but GET /routes no longer does (deleted, archived) still filters.
    for (const j of jobsData || []) if (j.route_id && !byId.has(Number(j.route_id))) byId.set(Number(j.route_id), j.route_name || `Route #${j.route_id}`);
    return Array.from(byId.entries()).sort((a, b) => a[1].localeCompare(b[1]));
  }, [routesData, jobsData]);

  // Search matches every word against client, address, title, service, route,
  // notes, status, date or job # ("matt ridgeway" → all of Matt's visits).
  const filtered = useMemo(() => {
    let list: any[] = jobsData || [];
    if (typeFilter) list = list.filter((j: any) => jobKind(j) === typeFilter);
    if (routeFilter === 'none') list = list.filter((j: any) => !j.route_id);
    else if (routeFilter) list = list.filter((j: any) => String(j.route_id) === routeFilter);
    const q = search.trim().toLowerCase();
    if (q) {
      const tokens = q.split(/\s+/).filter(Boolean);
      list = list.filter((j: any) => {
        const hay = [j.customer_name, j.todo_customer_name, j.address, j.city, j.title, j.service_type, j.service_tags, j.route_name, j.notes, j.status,
          j.scheduled_date, `#${j.id}`, String(j.id)].filter(Boolean).join(' ').toLowerCase();
        return tokens.every(t => hay.includes(t));
      });
    }
    // Date, then time; stops without a time keep their route order. Past reads newest first.
    const day = (j: any) => String(j.scheduled_date || '').slice(0, 10);
    const dir = when === 'past' || unpaidOnly ? -1 : 1;
    return [...list].sort((a: any, b: any) =>
      dir * day(a).localeCompare(day(b))
      || String(a.time_start || '99:99').localeCompare(String(b.time_start || '99:99'))
      || ((a.route_id || 0) - (b.route_id || 0))
      || ((a.route_seq ?? 9999) - (b.route_seq ?? 9999))
      || (a.id - b.id));
  }, [jobsData, search, typeFilter, routeFilter, when, unpaidOnly]);

  const nDone = filtered.filter((j: any) => j.status === 'complete').length;
  const nAhead = filtered.filter((j: any) => j.scheduled_date >= today && j.status !== 'complete' && j.status !== 'skipped').length;
  const anyFilter = !!(search.trim() || statusFilter || typeFilter || routeFilter);

  function clearFilters() {
    const next = new URLSearchParams(searchParams);
    for (const k of ['q', 'status', 'type', 'route']) next.delete(k);
    setSearchParams(next, { replace: true });
  }

  async function exportCSV() {
    const res = await api.get('/jobs/export-csv', { responseType: 'blob' });
    const url = URL.createObjectURL(new Blob([res.data]));
    const a = document.createElement('a');
    a.href = url; a.download = `jobs-${today}.csv`; a.click();
    URL.revokeObjectURL(url);
  }

  async function exportSheets() {
    setSheetsExporting(true); setSheetsMsg(null);
    try {
      const res = await api.post('/integrations/sheets/export/jobs');
      setSheetsMsg(res.data.message);
    } catch (err: any) {
      setSheetsMsg(err.response?.data?.message || 'Export failed');
    } finally { setSheetsExporting(false); }
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex justify-between items-center flex-wrap gap-3">
        <h1 className="text-2xl sm:text-3xl font-black uppercase tracking-tight text-gray-900">Jobs</h1>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={exportCSV}
            className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-600 hover:text-gray-800 border border-gray-300 rounded-md px-3 py-1.5 bg-white">
            <Download className="h-4 w-4" /> Export CSV
          </button>
          <button onClick={exportSheets} disabled={sheetsExporting}
            className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-600 hover:text-gray-800 border border-gray-300 rounded-md px-3 py-1.5 bg-white disabled:opacity-50">
            <UploadCloud className="h-4 w-4" /> {sheetsExporting ? 'Exporting…' : '→ Sheets'}
          </button>
          <button onClick={() => setShowCreate(true)} className="btn-primary text-sm">
            <Plus className="h-4 w-4 mr-1" /> New Job
          </button>
        </div>
      </div>

      {sheetsMsg && (
        <div className="rounded-md bg-blue-50 border border-blue-200 px-3 py-2 text-sm text-blue-800">{sheetsMsg}</div>
      )}

      {/* When: Day / Week / Month / All upcoming / Past */}
      {!unpaidOnly && (
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex flex-wrap gap-1.5" role="tablist" aria-label="When">
            {WHEN_OPTIONS.map(o => (
              <button key={o.key} type="button" role="tab" aria-selected={when === o.key} onClick={() => setParam('when', o.key)}
                className={`px-3 py-1.5 rounded-full text-sm font-medium border ${when === o.key ? 'bg-brand-600 border-brand-600 text-white' : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'}`}>
                {o.label}
              </button>
            ))}
          </div>
          {when === 'day' && (
            <input type="date" value={dayDate} onChange={e => setParam('date', e.target.value === today ? '' : e.target.value)}
              aria-label="Which day" className={selectCls} />
          )}
          <span className="text-xs text-gray-500">{range.label}</span>
        </div>
      )}

      {/* Search + status / type / route */}
      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative w-full sm:w-auto sm:flex-1 sm:min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
          <input type="search" placeholder="Search — client, address, title, job #" value={search} onChange={e => setParam('q', e.target.value)}
            className="w-full pl-9 pr-3 py-1.5 border border-gray-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500" />
        </div>
        <select value={statusFilter} onChange={e => setParam('status', e.target.value)} aria-label="Status" className={selectCls}>
          <option value="">All statuses</option>
          <option value="scheduled">Scheduled</option>
          <option value="in_progress">In progress</option>
          <option value="complete">Done</option>
          <option value="skipped">Skipped</option>
        </select>
        <select value={typeFilter} onChange={e => setParam('type', e.target.value)} aria-label="Type" className={selectCls}>
          <option value="">All types</option>
          <option value="mowing">Mowing</option>
          <option value="oneoff">One-off</option>
          <option value="project">Project</option>
          <option value="todo">To-do</option>
        </select>
        <select value={routeFilter} onChange={e => setParam('route', e.target.value)} aria-label="Route" className={selectCls}>
          <option value="">All routes</option>
          <option value="none">Not on a route</option>
          {routeOptions.map(([id, name]) => <option key={id} value={String(id)}>{name}</option>)}
        </select>
        {anyFilter && (
          <button type="button" onClick={clearFilters} className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700">
            <X className="h-3.5 w-3.5" /> Clear
          </button>
        )}
      </div>

      {unpaidOnly && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm text-amber-900">
          <b>Finished jobs with nobody credited for the pay.</b> Someone may well be assigned to the route — this is
          about the money: no pay entry was ever written for these, so profit reads high until you say who to pay.
          Open each one and mark who worked it.{' '}
          <Link to="/jobs" className="underline font-medium">Show all jobs</Link>
        </div>
      )}

      {!isLoading && (
        <p className="text-xs text-gray-500">
          {filtered.length} job{filtered.length === 1 ? '' : 's'}{search.trim() ? ` for “${search.trim()}”` : ''}
          {nDone ? ` · ${nDone} done` : ''}{nAhead ? ` · ${nAhead} ahead` : ''}
          {(when === 'past' || unpaidOnly) ? ' · newest first' : ''}
        </p>
      )}

      <div className="card overflow-hidden">
        {isLoading ? (
          <div className="p-6 text-center text-gray-500">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="p-6 text-center text-gray-500">
            {search.trim() ? `No jobs match “${search.trim()}”.` : anyFilter ? 'No jobs match these filters.' : `No jobs ${range.label}.`}
          </div>
        ) : (
          <ul className="divide-y divide-gray-100">
            {filtered.map((job: any) => {
              const effectivePay = job.crew_pay_is_manual ? job.crew_pay_override : job.estimated_crew_pay;
              const displayPrice = job.price_snapshot ?? job.property_price; // fall back to live property price for route jobs
              const kind = jobKind(job);
              const who = job.customer_name || job.todo_customer_name || job.title || 'Job';
              const serviceWord = kind === 'mowing' ? '' : fmtServiceType(job.service_type);
              // 'Irrigation · Irrigation check' says it twice; the title wins when it already names the service.
              const typeWord = serviceWord && String(job.title || '').toLowerCase().includes(serviceWord.toLowerCase()) ? '' : serviceWord;
              return (
                <li key={job.id}>
                  <Link to={`/jobs/${job.id}`} className="flex items-start sm:items-center gap-3 px-4 sm:px-6 py-3 hover:bg-gray-50">
                    {/* Date column on wide screens; on a phone the date sits in the line below the name. */}
                    <div className="hidden sm:block w-24 flex-shrink-0">
                      <p className="text-sm font-semibold text-gray-900">{fmtDate(job.scheduled_date)}</p>
                      {job.time_start && (
                        <p className={`text-xs ${job.time_is_firm ? 'text-red-600 font-bold' : 'text-gray-500'}`}>{fmtClock(job.time_start)}{job.time_is_firm ? ' ⏰' : ''}</p>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-sm font-medium text-gray-900 break-words">{who}</p>
                        {Number(job.span_days) > 1 && <span className="text-[11px] px-1.5 py-0.5 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200 font-medium">Day {job.span_day}/{job.span_days}</span>}
                        <span className={statusBadge[job.status] || 'badge-scheduled'}>
                          {String(job.status || 'scheduled').replace('_', ' ')}
                        </span>
                        <span className={`text-[11px] px-1.5 py-0.5 rounded border ${KIND_CHIP[kind]}`}>{KIND_LABEL[kind]}</span>
                        <RouteTag name={job.route_name} color={job.route_color} />
                        {String(job.service_tags || '').split(',').filter(Boolean).map((t: string) => (
                          <span key={t} className="text-[11px] px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200 capitalize">{t.replace('_', ' ')}</span>
                        ))}
                        {!!job.is_partial && (
                          <span className="text-[11px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 font-semibold">partial</span>
                        )}
                        {job.recurring_schedule_id && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-brand-100 text-brand-600 font-medium">
                            <RefreshCw className="h-2.5 w-2.5 mr-0.5" />
                            {freqLabel(job.schedule_frequency, job.schedule_custom_interval_days) || 'recurring'}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 mt-0.5">
                        <span className="sm:hidden">
                          {fmtDate(job.scheduled_date)}
                          {job.time_start ? <span className={job.time_is_firm ? 'text-red-600 font-bold' : ''}> · {fmtClock(job.time_start)}{job.time_is_firm ? ' ⏰' : ''}</span> : ''}
                          {' · '}
                        </span>
                        {[typeWord, job.title, job.address ? job.address.split(',')[0] : ''].filter(Boolean).join(' · ')}
                        {job.next_visit_date ? <span className="text-brand-600 font-medium"> · next {fmtShort(job.next_visit_date)}</span> : ''}
                      </p>
                      {(displayPrice || effectivePay) && (
                        <p className="text-xs mt-0.5 flex items-center gap-3">
                          {displayPrice && <span className="text-green-700 font-semibold flex items-center gap-0.5"><DollarSign className="h-3 w-3" />{displayPrice}</span>}
                          {effectivePay && <span className="text-gray-500">Crew ~${effectivePay}</span>}
                        </p>
                      )}
                    </div>
                    <ChevronRight className="h-4 w-4 text-gray-300 flex-shrink-0 self-center" />
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {showCreate && (
        <CreateJobModal
          date={when === 'day' ? dayDate : today}
          onClose={closeCreate}
          onCreated={() => { qc.invalidateQueries('jobs-list'); closeCreate(); }}
        />
      )}
    </div>
  );
}
